/**
 * Readiness recalculation planning — shared between web and mobile applications.
 *
 * `planReadinessRecalculation` is the PURE part of the "Recalculate Readiness Scores"
 * maintenance action: given already-fetched plain objects (the user's score records, the
 * current question bundle, and the app's domains) it computes
 *   1. which score records need their `topic_id` migrated to match the current bundle,
 *   2. which score records are orphans (the question was retired/renumbered out of the
 *      bundle) and should be deleted, and
 *   3. the recomputed readiness object — by DELEGATING to `calculateReadinessScores`
 *      (the single source of truth used on the answer hot path), never reimplementing it.
 *
 * The Firebase reads (fetch scores) and writes (save migrated scores, delete orphans,
 * persist readiness) stay in each app's API layer — core stays platform-agnostic.
 */

import { calculateReadinessScores } from './readinessScoreCalculator';

/**
 * Plan a readiness recalculation from already-fetched data.
 *
 * @param {Object[]} userScores    per-question score records: { question_id, topic_id, ... }
 * @param {Object[]} questionsList bundled exam questions: { id, topic_id, ... }
 * @param {Object[]} appDomains    domain/topic objects: { id, domain_name }
 * @returns {{
 *   scoresToSave: Object[],        // records whose topic_id changed (need a write)
 *   deletedQuestionIds: any[],     // question_ids no longer in the bundle (need a delete)
 *   migratedScores: Object[],      // all non-orphan records with corrected topic_ids
 *   readinessScores: Object,       // calculateReadinessScores(migratedScores, ...)
 *   updatedCount: number,
 *   deletedCount: number,
 *   hasChanges: boolean,
 *   bundleEmpty: boolean,          // questionsList was empty — no deletions produced
 *   allOrphaned: boolean,          // every record looked like an orphan — no deletions produced
 * }}
 */
export const planReadinessRecalculation = (userScores, questionsList, appDomains) => {
    const scores = Array.isArray(userScores) ? userScores : [];
    const questions = Array.isArray(questionsList) ? questionsList : [];
    const domains = Array.isArray(appDomains) ? appDomains : [];

    const bundleEmpty = questions.length === 0;

    // Build a question_id -> topic_id map from the current bundle. Keys are String-normalized
    // to mirror computeQuestionPools: score records persist question_id with mixed
    // string|number types, so a strict-type lookup would misclassify a string-typed record
    // as an orphan and (since orphans get deleted) silently destroy the user's answer.
    const questionTopicMap = new Map();
    questions.forEach((question) => {
        if (question.id !== undefined && question.id !== null && question.topic_id !== undefined) {
            questionTopicMap.set(String(question.id), question.topic_id);
        }
    });

    // Migrate topic_ids and identify orphaned records (question no longer in the bundle).
    const scoresToSave = [];
    const deletedQuestionIds = [];
    const migratedScores = [];

    for (const score of scores) {
        const questionId = score.question_id;
        const hasId = questionId !== undefined && questionId !== null;
        const currentTopicId = hasId ? questionTopicMap.get(String(questionId)) : undefined;

        if (currentTopicId !== undefined) {
            // Question still exists — correct its topic_id if it drifted.
            if (score.topic_id !== currentTopicId) {
                const updatedScore = { ...score, topic_id: currentTopicId };
                scoresToSave.push(updatedScore);
                migratedScores.push(updatedScore);
            } else {
                migratedScores.push(score);
            }
        } else if (hasId && !bundleEmpty) {
            // Question genuinely retired/renumbered out of a populated bundle — orphan.
            deletedQuestionIds.push(questionId);
        } else {
            // Malformed record (no question_id) or an empty bundle — NEVER delete; keep it.
            // The bundle-anchored readiness calc ignores unmatched records safely.
            migratedScores.push(score);
        }
    }

    // Catastrophe guard: never mass-delete. If the bundle was empty (e.g. questions not yet
    // loaded) or every record looked like an orphan, refuse all deletions and let callers
    // bail via the flags rather than wiping the user's entire score history.
    const allOrphaned = scores.length > 0 && deletedQuestionIds.length === scores.length;
    if (bundleEmpty || allOrphaned) {
        return {
            scoresToSave: [],
            deletedQuestionIds: [],
            migratedScores: scores,
            readinessScores: calculateReadinessScores(scores, questions, domains),
            updatedCount: 0,
            deletedCount: 0,
            hasChanges: false,
            bundleEmpty,
            allOrphaned,
        };
    }

    // Recompute readiness from the honest migrated set (no fallback masking).
    const readinessScores = calculateReadinessScores(migratedScores, questions, domains);

    return {
        scoresToSave,
        deletedQuestionIds,
        migratedScores,
        readinessScores,
        updatedCount: scoresToSave.length,
        deletedCount: deletedQuestionIds.length,
        hasChanges: scoresToSave.length > 0 || deletedQuestionIds.length > 0,
        bundleEmpty: false,
        allOrphaned: false,
    };
};
